import { ApiService } from "../../api/api-service";
import { JourneyStudent, JourneyInstructor } from "../Journey/journey.types";
import { getInstructorLeaveCount, getAllInstructorLeaveCounts } from "./journeyLeave.utils";

/**
 *  ENHANCED: Sync instructor change from Journey to Batch Table
 *  Now recalculates leave counts and preserves ALL existing instructor fields.
 */
export const syncInstructorToBatch = async (
  batchNo: string,
  journeyInstructors: JourneyInstructor[]
) => {
  try {
    const batchResponse = await ApiService.getInstance().get(`/batches/${batchNo}`);
    const currentBatch = batchResponse.data.data;

    if (!currentBatch) {
      throw new Error(`Batch ${batchNo} not found`);
    }

    const list = currentBatch.instructors || currentBatch.instructor;

    // Recalculate leave counts for ALL instructors in a single pass
    const emails = journeyInstructors.map((ins) => ins.email).filter(Boolean);
    const leaveCounts = await getAllInstructorLeaveCounts(batchNo, emails);

    const batchInstructors = journeyInstructors.map((ins) => {
      const existing = list?.find((i: any) => i.email === ins.email);
      const emailKey = (ins.email || "").toLowerCase();
      const leaveCount = leaveCounts.get(emailKey) ?? existing?.trainerLeaves ?? 0;

      return {
        name: ins.name,
        email: ins.email,
        dateOfAssign: existing?.dateOfAssign || new Date().toISOString().split('T')[0],
        mouHours: existing?.mouHours || "0",
        mouAmount: existing?.mouAmount || 0,
        trainerLeaves: leaveCount,
        attendedHours: ins.instructorClasses || existing?.attendedHours || "0",
        batchTiming: existing?.batchTiming || "",
        // Preserve additional fields
        paymentTermsText: existing?.paymentTermsText || "",
        numOfStud: existing?.numOfStud || 0,
      };
    });

    //  FIXED: Preserve current batch trainer instead of blindly using last instructor
    //  After merge, instructor array order is NOT guaranteed.
    const currentTrainerEmail = (currentBatch.trainerEmail || "").toLowerCase();
    const matchingInstructor = currentTrainerEmail
      ? journeyInstructors.find(
          (i) => (i.email || "").toLowerCase() === currentTrainerEmail
        )
      : null;
    const trainerToUse = matchingInstructor || journeyInstructors[journeyInstructors.length - 1];

    const updatedBatch = {
      ...currentBatch,
      instructors: batchInstructors,
      instructor: batchInstructors,
      trainerName: trainerToUse.name,
      trainerEmail: trainerToUse.email,
    };

    await ApiService.getInstance().put(`/batches/${batchNo}`, updatedBatch);

    return { success: true };
  } catch (error) {
    console.error(" Error syncing instructors:", error);
    throw error;
  }
};

/**
 *  ENHANCED: Sync students from Journey to Batch Table
 */
export const syncStudentsToBatch = async (
  batchNo: string,
  journeyStudents: JourneyStudent[]
) => {
  try {
    // console.log(" Starting syncStudentsToBatch...");
    // console.log("Journey students:", journeyStudents);

    const batchResponse = await ApiService.getInstance().get(`/batches/${batchNo}`);
    const currentBatch = batchResponse.data.data;

    if (!currentBatch) {
      throw new Error(`Batch ${batchNo} not found`);
    }

    const batchStudents = journeyStudents.map((js) => {
      const existingStudent = currentBatch.students?.find(
        (s: any) => s.email === js.email
      );

      // console.log(` Mapping student ${js.name}:`, {
      //   journeyStudentClasses: js.studentClasses,
      //   existingStudentClasses: existingStudent?.studentClasses,
      //   willSetTo: js.studentClasses
      // });

      return {
        name: js.name,
        email: js.email,
        studentClasses: js.studentClasses,
        amount: existingStudent?.amount || 0,
        currency: existingStudent?.currency || "INR",
        received: existingStudent?.received || 0,
        balance: existingStudent?.balance || 0,
        referral: existingStudent?.referral || 0,
        paymentStatus: existingStudent?.paymentStatus || "Not Applicable",
        comment: existingStudent?.comment || "",
        //  Persist student status so it survives being reloaded from the Batch Table
        studentStatus:
          (js as any).studentStatus ||
          existingStudent?.studentStatus ||
          "Active",
      };
    });

    const updatedBatch = {
      ...currentBatch,
      students: batchStudents,
    };

    await ApiService.getInstance().put(`/batches/${batchNo}`, updatedBatch);

    // console.log(" Students synced successfully");
    return { success: true };
  } catch (error) {
    console.error(" Error syncing students to batch:", error);
    throw error;
  }
};

/**
 * Get full batch details for sync
 */
export const getBatchDetails = async (
  batchNo: string
): Promise<any | null> => {
  try {
    // console.log(" Fetching batch details for:", batchNo);
    const response = await ApiService.getInstance().get(`/batches/${batchNo}`);

    if (response.data && response.data.data) {
      // console.log(" Batch details fetched:", {
      //   batch_no: response.data.data.batch_no,
      //   numSessions: response.data.data.numSessions,
      //   instructorsCount: response.data.data.instructors?.length || 0,
      //   studentsCount: response.data.data.students?.length || 0,
      // });
      
      //  Log instructor details
      const instructors = response.data.data.instructors || response.data.data.instructor;
      if (instructors && instructors.length > 0) {
        // console.log("Batch instructors details:");
        instructors.forEach((i: any) => {
          // console.log(`  - ${i.name}: attendedHours = ${i.attendedHours}`);
        });
      }

      return response.data.data;
    }

    return null;
  } catch (error) {
    console.error(" Error getting batch details:", error);
    return null;
  }
};

/**
 *  ENHANCED: Get students from batch for auto-filling in journey
 */
export const getStudentsFromBatch = async (
  batchNo: string
): Promise<JourneyStudent[]> => {
  try {
    // console.log(" Fetching students from batch:", batchNo);
    const batchResponse = await ApiService.getInstance().get(`/batches/${batchNo}`);
    const batch = batchResponse.data.data;

    if (!batch || !batch.students) {
      return [];
    }

    const students = batch.students.map((s: any) => ({
      name: s.name,
      email: s.email,
      studentClasses: s.studentClasses || "",
      attendance: "Present",
      studentStatus: s.studentStatus || "Active",
    }));

    // console.log(" Students loaded:", students.length);
    return students;
  } catch (error) {
    console.error(" Error getting students from batch:", error);
    return [];
  }
};

/**
 *  ENHANCED: Sync journey creation to batch (update batch with journey data)
 */
export const syncJourneyCreationToBatch = async (
  batchNo: string,
  journeyData: {
    trainerName: string;
    trainerEmail: string;
    students: JourneyStudent[];
    instructors?: JourneyInstructor[];
    numSessions?: number;
    batchStatus?: string;
  }
) => {
  try {
    // console.log(" Starting syncJourneyCreationToBatch...");
    // console.log(" Journey data:", {
    //   numSessions: journeyData.numSessions,
    //   batchStatus: journeyData.batchStatus,
    //   instructorsCount: journeyData.instructors?.length || 0,
    //   studentsCount: journeyData.students?.length || 0,
    // });

    const batchResponse = await ApiService.getInstance().get(`/batches/${batchNo}`);
    const currentBatch = batchResponse.data.data;

    if (!currentBatch) {
      throw new Error(`Batch ${batchNo} not found`);
    }

    // console.log(" Current batch instructors:", currentBatch.instructors?.length || 0);

    // Map students
    const batchStudents = journeyData.students.map((js) => {
      const existingStudent = currentBatch.students?.find(
        (s: any) => s.email === js.email
      );

      return {
        name: js.name,
        email: js.email,
        studentClasses: js.studentClasses,
        amount: existingStudent?.amount || 0,
        currency: existingStudent?.currency || "INR",
        received: existingStudent?.received || 0,
        balance: existingStudent?.balance || 0,
        referral: existingStudent?.referral || 0,
        paymentStatus: existingStudent?.paymentStatus || "Not Applicable",
        comment: existingStudent?.comment || "",
        //  Persist student status so it survives being reloaded from the Batch Table
        studentStatus:
          (js as any).studentStatus ||
          existingStudent?.studentStatus ||
          "Active",
      };
    });

    //  CRITICAL FIX: Only update instructorClasses for CURRENT trainer
    const list = currentBatch.instructors || currentBatch.instructor;
    const batchInstructors = list?.map((batchIns: any) => {
      const journeyIns = journeyData.instructors?.find(
        (ji) => ji.email.toLowerCase() === batchIns.email.toLowerCase()
      );

      //  Only update if this instructor is in the journey data
      const isCurrentTrainer = batchIns.email.toLowerCase() === journeyData.trainerEmail.toLowerCase();

      const newAttendedHours = isCurrentTrainer && journeyIns
        ? (journeyIns.instructorClasses || batchIns.attendedHours || "0")
        : batchIns.attendedHours;

      return {
        ...batchIns,
        attendedHours: newAttendedHours,
      };
    }) || list;

    // Recalculate trainerLeaves for ALL instructors in a single pass (efficient)
    const allEmails = (batchInstructors || []).map((ins: any) => ins.email).filter(Boolean);
    const leaveCounts = await getAllInstructorLeaveCounts(batchNo, allEmails);

    const instructorsWithLeaves = (batchInstructors || []).map((ins: any) => {
      const emailKey = (ins.email || "").toLowerCase();
      const leaveCount = leaveCounts.get(emailKey) ?? ins.trainerLeaves ?? 0;
      return { ...ins, trainerLeaves: leaveCount };
    });

    //  CRITICAL FIX: Determine batch status - NEVER use Leave/Holiday
    const validBatchStatuses = [
      "Not Started",
      "On-going",
      "Completed",
      "Hold",
      "Absconded",
      "Discarded",
    ];

    let finalBatchStatus = currentBatch.batchStatus;

    //  Only update if a valid status is provided
    if (journeyData.batchStatus && validBatchStatuses.includes(journeyData.batchStatus)) {
      finalBatchStatus = journeyData.batchStatus;
    }

    //  Ensure batch status is never Leave/Holiday/Not Schedule
    if (finalBatchStatus === "Leave" || finalBatchStatus === "Holiday" || finalBatchStatus === "Not Schedule") {
      finalBatchStatus = currentBatch.batchStatus || "On-going";
    }

    //  Final validation
    if (!validBatchStatuses.includes(finalBatchStatus)) {
      finalBatchStatus = "On-going";
    }

    const updatedBatch = {
      ...currentBatch,
      trainerName: journeyData.trainerName,
      trainerEmail: journeyData.trainerEmail,
      students: batchStudents,
      numSessions: journeyData.numSessions !== undefined
        ? journeyData.numSessions
        : currentBatch.numSessions,
      batchStatus: finalBatchStatus, //  Always valid status
      instructors: instructorsWithLeaves,
      instructor: instructorsWithLeaves,
    };

    // console.log(" Updating batch with:", {
    //   instructorsCount: batchInstructors.length,
    //   batchStatus: updatedBatch.batchStatus,
    // });

    await ApiService.getInstance().put(`/batches/${batchNo}`, updatedBatch);

    // console.log(" Batch synced successfully:", {
    //   batchNo,
    //   numSessions: updatedBatch.numSessions,
    //   batchStatus: updatedBatch.batchStatus,
    //   instructorsUpdated: batchInstructors.length,
    //   studentsUpdated: batchStudents.length
    // });

    return { success: true };
  } catch (error) {
    console.error("❌ Error syncing journey creation to batch:", error);
    throw error;
  }
};

// export const syncJourneyCreationToBatch = async (
//   batchNo: string,
//   journeyData: {
//     trainerName: string;
//     trainerEmail: string;
//     students: JourneyStudent[];
//     instructors?: JourneyInstructor[];
//     numSessions?: number;
//     batchStatus?: string;
//   }
// ) => {
//   try {
//     console.log(" Starting syncJourneyCreationToBatch...");

//     const batchResponse = await ApiService.getInstance().get(`/batches/${batchNo}`);
//     const currentBatch = batchResponse.data.data;

//     if (!currentBatch) {
//       throw new Error(`Batch ${batchNo} not found`);
//     }

//     // Map students
//     const batchStudents = journeyData.students.map((js) => {
//       const existingStudent = currentBatch.students?.find(
//         (s: any) => s.email === js.email
//       );

//       return {
//         name: js.name,
//         email: js.email,
//         studentClasses: js.studentClasses,
//         amount: existingStudent?.amount || 0,
//         currency: existingStudent?.currency || "INR",
//         received: existingStudent?.received || 0,
//         balance: existingStudent?.balance || 0,
//         referral: existingStudent?.referral || 0,
//         paymentStatus: existingStudent?.paymentStatus || "Not Applicable",
//         comment: existingStudent?.comment || "",
//       };
//     });

//     // Map instructors
//     const list = currentBatch.instructors || currentBatch.instructor;
//     const batchInstructors = list?.map((batchIns: any) => {
//       const journeyIns = journeyData.instructors?.find(
//         (ji) => ji.email === batchIns.email
//       );

//       // Only update if this is the current trainer
//       const isCurrentTrainer = batchIns.email.toLowerCase() === journeyData.trainerEmail.toLowerCase();

//       const newAttendedHours = isCurrentTrainer && journeyIns
//         ? (journeyIns.instructorClasses || batchIns.attendedHours || "0")
//         : batchIns.attendedHours;

//       return {
//         ...batchIns,
//         attendedHours: newAttendedHours,
//       };
//     }) || list;

//     //  CRITICAL FIX: Determine batch status - NEVER use Leave/Holiday
//     const validBatchStatuses = [
//       "Not Started",
//       "On-going",
//       "Completed",
//       "Hold",
//       "Absconded",
//       "Discarded",
//     ];

//     let finalBatchStatus = currentBatch.batchStatus;

//     //  Only update batch status if a valid status is provided
//     if (journeyData.batchStatus && validBatchStatuses.includes(journeyData.batchStatus)) {
//       finalBatchStatus = journeyData.batchStatus;
//     }

//     //  Ensure batch status is never Leave/Holiday
//     if (finalBatchStatus === "Leave" || finalBatchStatus === "Holiday") {
//       finalBatchStatus = "On-going";
//     }

//     //  Final validation
//     if (!validBatchStatuses.includes(finalBatchStatus)) {
//       finalBatchStatus = "On-going";
//     }

//     const updatedBatch = {
//       ...currentBatch,
//       trainerName: journeyData.trainerName,
//       trainerEmail: journeyData.trainerEmail,
//       students: batchStudents,
//       numSessions: journeyData.numSessions !== undefined
//         ? journeyData.numSessions
//         : currentBatch.numSessions,
//       batchStatus: finalBatchStatus, //  Always valid status
//       instructors: batchInstructors,
//       instructor: batchInstructors,
//     };

//     await ApiService.getInstance().put(`/batches/${batchNo}`, updatedBatch);

//     console.log(" Batch synced successfully:", {
//       batchNo,
//       batchStatus: updatedBatch.batchStatus,
//     });

//     return { success: true };
//   } catch (error) {
//     console.error(" Error syncing journey creation to batch:", error);
//     throw error;
//   }
// };
// export const syncJourneyCreationToBatch = async (
//   batchNo: string,
//   journeyData: {
//     trainerName: string;
//     trainerEmail: string;
//     students: JourneyStudent[];
//     instructors?: JourneyInstructor[];
//     numSessions?: number;
//     batchStatus?: string;
//   }
// ) => {
//   try {
//     console.log(" Starting syncJourneyCreationToBatch...");
//     console.log("Journey data:", {
//       numSessions: journeyData.numSessions,
//       batchStatus: journeyData.batchStatus,
//       instructorsCount: journeyData.instructors?.length || 0,
//       studentsCount: journeyData.students?.length || 0,
//     });

//     const batchResponse = await ApiService.getInstance().get(`/batches/${batchNo}`);
//     const currentBatch = batchResponse.data.data;

//     if (!currentBatch) {
//       throw new Error(`Batch ${batchNo} not found`);
//     }

//     // Map students
//     const batchStudents = journeyData.students.map((js) => {
//       const existingStudent = currentBatch.students?.find(
//         (s: any) => s.email === js.email
//       );

//       return {
//         name: js.name,
//         email: js.email,
//         studentClasses: js.studentClasses,
//         amount: existingStudent?.amount || 0,
//         currency: existingStudent?.currency || "INR",
//         received: existingStudent?.received || 0,
//         balance: existingStudent?.balance || 0,
//         referral: existingStudent?.referral || 0,
//         paymentStatus: existingStudent?.paymentStatus || "Not Applicable",
//         comment: existingStudent?.comment || "",
//       };
//     });

//     //  CRITICAL: Map instructors with attendedHours
//     const list = currentBatch.instructors || currentBatch.instructor;
//     const batchInstructors = list?.map((batchIns: any) => {
//       const journeyIns = journeyData.instructors?.find(
//         (ji) => ji.email === batchIns.email
//       );

//       const newAttendedHours = journeyIns
//         ? (journeyIns.instructorClasses || batchIns.attendedHours || "0")
//         : batchIns.attendedHours;

//       console.log(` Syncing instructor ${batchIns.name}:`, {
//         journeyInstructorClasses: journeyIns?.instructorClasses,
//         currentAttendedHours: batchIns.attendedHours,
//         newAttendedHours: newAttendedHours
//       });

//       return {
//         ...batchIns,
//         attendedHours: newAttendedHours,
//       };
//     }) || list;

//     // Determine batch status
//     let finalBatchStatus = currentBatch.batchStatus;
//     if (journeyData.batchStatus) {
//       if (journeyData.batchStatus === "Leave" || journeyData.batchStatus === "Holiday") {
//         finalBatchStatus = journeyData.batchStatus;
//       } else if (journeyData.batchStatus === "On-going" || journeyData.batchStatus === "Completed") {
//         finalBatchStatus = journeyData.batchStatus;
//       }
//     }

//     const updatedBatch = {
//       ...currentBatch,
//       trainerName: journeyData.trainerName,
//       trainerEmail: journeyData.trainerEmail,
//       students: batchStudents,
//       numSessions: journeyData.numSessions !== undefined
//         ? journeyData.numSessions
//         : currentBatch.numSessions,
//       batchStatus: finalBatchStatus,
//       instructors: batchInstructors,
//       instructor: batchInstructors,
//     };

//     await ApiService.getInstance().put(`/batches/${batchNo}`, updatedBatch);

//     console.log(" Batch synced successfully:", {
//       batchNo,
//       numSessions: updatedBatch.numSessions,
//       batchStatus: updatedBatch.batchStatus,
//       instructorsUpdated: batchInstructors.length,
//       studentsUpdated: batchStudents.length
//     });

//     return { success: true };
//   } catch (error) {
//     console.error(" Error syncing journey creation to batch:", error);
//     throw error;
//   }
// };